import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  DatePicker,
  FileUpload,
  Modal,
  PageHeader,
  Radio,
  RadioGroup,
  Select,
  Tag,
  TextInput,
  Textarea,
  Toggle,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import GiftCardPreview from "./GiftCardPreview";
import { isoDate, parseIsoDate } from "../../GiftCardPurchase/useGiftCardPurchase";
import GiftCardSalesOffAlert, { giftCardSalesShape, turnOnFailedMessage, turnOnGiftCardSales } from "./GiftCardSalesOffAlert";
import {
  SCOPE_TEAM,
  SCOPE_VENUES,
  STATUS_ARCHIVED,
  STATUS_COLORS,
  STATUS_DRAFT,
  STATUS_LABELS,
  formatCents,
} from "../giftCardFormat";
import "../styles/giftCardProducts.css";

const TERMS_MAX_LENGTH = 5000;

const FIELD_ERROR_KEYS = {
  name: ["name"],
  artwork: ["artwork", "artwork_content_type", "artwork_file_size"],
  terms: ["terms"],
  amounts: ["denominations_cents"],
  customRange: ["custom_min_cents", "custom_max_cents"],
  scope: ["scope"],
  venues: ["venue_ownerships"],
  payout: ["payout_venue_ownership", "payout_venue_ownership_id"],
  expiry: ["expires_in_months"],
  stock: ["stock_level"],
  perOrder: ["per_order_limit"],
  bonusAmount: ["bonus_amount_cents"],
  bonusMin: ["bonus_min_amount_cents"],
  bonusStartsOn: ["bonus_starts_on"],
  bonusEndsOn: ["bonus_ends_on"],
  bonusExpiry: ["bonus_expires_in_months"],
};

const NEVER_EXPIRES = "never";
const DEFAULT_BONUS_EXPIRY = "24";
const bonusExpiryLabel = (choice) => (choice === NEVER_EXPIRES ? "Never" : `${choice} months after issue`);

const digitsOnly = (value) => String(value || "").replace(/[^0-9]/g, "");
const centsToDollars = (cents) => (cents ? String(Math.round(cents / 100)) : "");
const dollarsToCents = (dollars) => (dollars === "" ? "" : String(parseInt(dollars, 10) * 100));
const checkedFrom = (value) => (value && value.target ? value.target.checked : Boolean(value));

const formFrom = (product) => ({
  name: product.name || "",
  terms: product.terms || "",
  denominationsCents: product.denominationsCents || [],
  customAmounts: Boolean(product.customMinCents && product.customMaxCents),
  customMin: centsToDollars(product.customMinCents),
  customMax: centsToDollars(product.customMaxCents),
  scope: product.scope,
  venueOwnershipIds: product.venueOwnershipIds || [],
  payoutVenueOwnershipId: product.payoutVenueOwnershipId,
  expires: Boolean(product.expiresInMonths),
  expiresInMonths: product.expiresInMonths ? String(product.expiresInMonths) : "",
  stockLevel: product.stockLevel == null ? "" : String(product.stockLevel),
  perOrderLimit: product.perOrderLimit == null ? "" : String(product.perOrderLimit),
  bonus: Boolean(product.bonusAmountCents),
  bonusAmount: centsToDollars(product.bonusAmountCents),
  bonusMin: centsToDollars(product.bonusMinAmountCents),
  bonusStartsOn: product.bonusStartsOn ? parseIsoDate(product.bonusStartsOn) : null,
  bonusEndsOn: product.bonusEndsOn ? parseIsoDate(product.bonusEndsOn) : null,
  bonusExpiresIn: product.bonusAmountCents
    ? product.bonusExpiresInMonths
      ? String(product.bonusExpiresInMonths)
      : NEVER_EXPIRES
    : DEFAULT_BONUS_EXPIRY,
});

const expiryHint = (minExpiryMonths, stateName) =>
  stateName
    ? `Paid cards in ${stateName} can't expire sooner than ${minExpiryMonths} months (${minExpiryMonths / 12} years).`
    : `Paid cards can't expire sooner than ${minExpiryMonths} months (5 years).`;

export const buildFormData = (form, artworkFile, minExpiryMonths) => {
  const data = new FormData();
  const field = (key, value) => data.append(`gift_card_product[${key}]`, value == null ? "" : value);

  field("name", form.name);
  field("terms", form.terms);
  field("scope", form.scope);
  field("payout_venue_ownership_id", form.payoutVenueOwnershipId);
  field("custom_min_cents", form.customAmounts ? dollarsToCents(form.customMin) : "");
  field("custom_max_cents", form.customAmounts ? dollarsToCents(form.customMax) : "");
  field("expires_in_months", form.expires ? form.expiresInMonths || String(minExpiryMonths) : "");
  field("stock_level", form.stockLevel);
  field("per_order_limit", form.perOrderLimit);
  field("bonus_amount_cents", form.bonus ? dollarsToCents(form.bonusAmount) : "");
  field("bonus_min_amount_cents", form.bonus ? dollarsToCents(form.bonusMin) : "");
  field("bonus_starts_on", form.bonus && form.bonusStartsOn ? isoDate(form.bonusStartsOn) : "");
  field("bonus_ends_on", form.bonus && form.bonusEndsOn ? isoDate(form.bonusEndsOn) : "");
  field("bonus_expires_in_months", form.bonus && form.bonusExpiresIn !== NEVER_EXPIRES ? form.bonusExpiresIn : "");

  const amounts = form.denominationsCents.length > 0 ? form.denominationsCents : [""];
  amounts.forEach((cents) => field("denominations_cents][", cents));
  const venueIds = form.scope === SCOPE_VENUES && form.venueOwnershipIds.length > 0 ? form.venueOwnershipIds : [""];
  venueIds.forEach((id) => field("venue_ownership_ids][", id));

  if (artworkFile) data.append("gift_card_product[artwork]", artworkFile);
  return data;
};

const firstError = (errors, key) => {
  const messages = FIELD_ERROR_KEYS[key].flatMap((attribute) => errors[attribute] || []);
  return messages.length > 0 ? messages.join(", ") : null;
};

const ScopeOption = ({ title, description }) => (
  <span>
    <span className="gift-card-editor-option-title">{title}</span>
    <span className="gift-card-editor-option-description">{description}</span>
  </span>
);

ScopeOption.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const Section = ({ title, description, children }) => (
  <Card padding="md">
    <div className="gift-card-editor-section">
      <div>
        <h2 className="gift-card-editor-section-title">{title}</h2>
        {description && <p className="gift-card-editor-hint">{description}</p>}
      </div>
      {children}
    </div>
  </Card>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node,
};

const GiftCardProductEditor = ({
  product: initialProduct,
  teamName,
  currency,
  venueOwnerships,
  productsPath,
  createPath,
  minAmountCents,
  maxAmountCents,
  minExpiryMonths,
  bonusExpiryChoices,
  paidExpiryBanned = false,
  expiryStateName = null,
  expiryLimits = {},
  giftCardSales,
}) => {
  const [product, setProduct] = useState(initialProduct);
  const [form, setForm] = useState(() => formFrom(initialProduct));
  const expiryLimit = expiryLimits[form.payoutVenueOwnershipId] || { minExpiryMonths, paidExpiryBanned, expiryStateName };
  const payoutVenueChange = (value) => {
    const minimum = (expiryLimits[value] || { minExpiryMonths }).minExpiryMonths;
    if (!form.expires) return { payoutVenueOwnershipId: value };
    if ((parseInt(form.expiresInMonths, 10) || 0) >= minimum) return { payoutVenueOwnershipId: value };
    return { payoutVenueOwnershipId: value, expiresInMonths: String(minimum) };
  };
  const [newAmount, setNewAmount] = useState("");
  const [amountError, setAmountError] = useState(null);
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreviewUrl, setArtworkPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [fullMessages, setFullMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [salesEnabled, setSalesEnabled] = useState(giftCardSales.enabled);
  const [turningOnSales, setTurningOnSales] = useState(false);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  useEffect(() => () => artworkPreviewUrl && URL.revokeObjectURL(artworkPreviewUrl), [artworkPreviewUrl]);

  const update = (changes) => setForm((current) => ({ ...current, ...changes }));
  const isNew = !product.id;
  const isDraft = product.status === STATUS_DRAFT;
  const isArchived = product.status === STATUS_ARCHIVED;

  const venueOptions = useMemo(
    () => venueOwnerships.map((venueOwnership) => ({ value: venueOwnership.id, label: venueOwnership.name })),
    [venueOwnerships]
  );
  const selectedVenueNames = venueOwnerships
    .filter((venueOwnership) => form.venueOwnershipIds.includes(venueOwnership.id))
    .map((venueOwnership) => venueOwnership.name);

  const addAmount = () => {
    if (newAmount === "") return;
    const cents = parseInt(newAmount, 10) * 100;
    if (cents < minAmountCents || cents > maxAmountCents) {
      setAmountError(
        `Amounts must be between ${formatCents(minAmountCents, currency, { whole: true })} and ${formatCents(maxAmountCents, currency, { whole: true })}.`
      );
      return;
    }
    setAmountError(null);
    setNewAmount("");
    if (form.denominationsCents.includes(cents)) return;
    update({ denominationsCents: [...form.denominationsCents, cents].sort((a, b) => a - b) });
  };

  const removeAmount = (cents) =>
    update({ denominationsCents: form.denominationsCents.filter((amount) => amount !== cents) });

  const selectArtwork = (files) => {
    const file = files[0];
    if (!file) return;
    setArtworkFile(file);
    setArtworkPreviewUrl(URL.createObjectURL(file));
  };

  const goToProducts = () => {
    window.location.href = productsPath;
  };

  const turnOnSales = async () => {
    await turnOnGiftCardSales(giftCardSales.settingsPath);
    setSalesEnabled(true);
  };

  const turnOnSalesFromBanner = async () => {
    setTurningOnSales(true);
    try {
      await turnOnSales();
      toastSuccess("Gift card sales are on.");
    } catch (requestError) {
      toastError(turnOnFailedMessage(requestError));
    } finally {
      setTurningOnSales(false);
    }
  };

  const publishedMessage = async (turnOn) => {
    if (salesEnabled) return toastSuccess("Gift card published.");
    if (!turnOn) return toastSuccess("Gift card published. Buyers can't buy it until gift card sales are on.");
    try {
      await turnOnSales();
      toastSuccess("Gift card published and gift card sales are on.");
    } catch (requestError) {
      toastError(turnOnFailedMessage(requestError, "Gift card published, but we couldn't turn gift card sales on."));
    }
  };

  const publish = () => {
    if (!salesEnabled && !giftCardSales.stillOnGiftUp) {
      setConfirmingPublish(true);
    } else {
      save(true);
    }
  };

  const confirmPublish = (turnOn) => {
    setConfirmingPublish(false);
    save(true, { turnOn });
  };

  const save = async (publish, { turnOn = false } = {}) => {
    setSaving(true);
    setErrors({});
    setFullMessages([]);
    const data = buildFormData(form, artworkFile, expiryLimit.minExpiryMonths);
    if (publish) data.append("publish", "true");
    try {
      const response = isNew ? await client.post(createPath, data) : await client.patch(product.updatePath, data);
      setProduct(response.data);
      setForm(formFrom(response.data));
      setArtworkFile(null);
      setArtworkPreviewUrl(null);
      if (isNew) window.history.replaceState({}, "", response.data.editPath);
      if (publish) {
        await publishedMessage(turnOn);
      } else {
        toastSuccess("Gift card saved.");
      }
    } catch (requestError) {
      const body = requestError?.response?.data;
      if (body?.errors) {
        setErrors(body.errors);
        setFullMessages(body.fullMessages || []);
      } else {
        toastError("Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const title = isNew ? "New gift card" : isArchived ? "Archived gift card" : "Edit gift card";
  const crumb = isNew ? "New product" : product.name;
  const artworkUrl = artworkPreviewUrl || product.artworkUrl;

  const editorActions = isDraft ? (
    <div className="gift-card-editor-actions">
      <Button variant="secondary" onClick={() => save(false)} disabled={saving}>
        Save draft
      </Button>
      <Button variant="primary" onClick={publish} disabled={saving || turningOnSales}>
        Publish
      </Button>
    </div>
  ) : (
    <div className="gift-card-editor-actions">
      <Button variant="secondary" onClick={goToProducts}>
        Cancel
      </Button>
      <Button variant="primary" onClick={() => save(false)} disabled={saving}>
        Save changes
      </Button>
    </div>
  );

  const actions = isArchived ? (
    <Button variant="secondary" onClick={goToProducts}>
      Back to gift cards
    </Button>
  ) : (
    editorActions
  );

  return (
    <div className="gift-card-editor">
      <PageHeader
        breadcrumbs={<Breadcrumbs items={[{ label: "Gift cards", href: productsPath }, { label: crumb }]} />}
        title={
          <span className="gift-card-editor-title">
            {title}
            <Badge color={STATUS_COLORS[product.status]} dot size="sm">
              {STATUS_LABELS[product.status]}
            </Badge>
          </span>
        }
        actions={actions}
      />

      {!salesEnabled && !isArchived && (
        <GiftCardSalesOffAlert
          sales={giftCardSales}
          canManage
          turningOn={turningOnSales || saving}
          onTurnOn={turnOnSalesFromBanner}
        />
      )}

      {product.soldCount > 0 && (
        <div className="gift-card-editor-alert">
          <Alert
            color="brand"
            title="This product has sold cards"
            description={`Changes apply to new sales only. The ${new Intl.NumberFormat("en-US").format(product.soldCount)} cards already sold keep the amounts, venues and expiry they were sold with.`}
          />
        </div>
      )}

      {fullMessages.length > 0 && (
        <div className="gift-card-editor-alert" data-testid="gift-card-editor-errors">
          <Alert
            color="error"
            title={`Fix ${fullMessages.length} ${fullMessages.length === 1 ? "thing" : "things"} before saving`}
            description={fullMessages.join(" ")}
          />
        </div>
      )}

      {isArchived && (
        <div className="gift-card-editor-alert" data-testid="gift-card-editor-archived">
          <Alert
            color="gray"
            title="This product is archived"
            description="It's off sale and can't be changed. Cards already sold keep working."
          />
        </div>
      )}

      <div className="gift-card-editor-layout">
        <fieldset className="gift-card-editor-form" disabled={isArchived}>
          <Section title="Details">
            <TextInput
              id="gift-card-product-name"
              label="Name"
              placeholder="e.g. The Vogue Gift Card"
              value={form.name}
              maxLength={255}
              onChange={(event) => update({ name: event.target.value })}
              error={firstError(errors, "name")}
            />
            <div className="gift-card-editor-field">
              <span className="gift-card-editor-label">Artwork</span>
              <div className="gift-card-editor-artwork">
                <div
                  className="gift-card-editor-artwork-thumb"
                  style={artworkUrl ? { backgroundImage: `url("${artworkUrl}")` } : undefined}
                  data-testid="gift-card-artwork-thumb"
                />
                <div className="gift-card-editor-artwork-upload">
                  <FileUpload
                    accept="image/png,image/jpeg"
                    hint="PNG or JPG, 1600 × 1000 px"
                    files={artworkFile ? [{ id: "artwork", name: artworkFile.name, size: artworkFile.size, progress: 100 }] : []}
                    onFilesSelected={selectArtwork}
                    onRemove={() => {
                      setArtworkFile(null);
                      setArtworkPreviewUrl(null);
                    }}
                  />
                </div>
              </div>
              {firstError(errors, "artwork") && <p className="gift-card-editor-error">{firstError(errors, "artwork")}</p>}
            </div>
            <Textarea
              id="gift-card-product-terms"
              label="Terms"
              rows={3}
              maxLength={TERMS_MAX_LENGTH}
              value={form.terms}
              onChange={(event) => update({ terms: event.target.value })}
              hint="Shown on the purchase page, the gift email and at checkout."
              error={firstError(errors, "terms")}
            />
          </Section>

          <Section
            title="Amounts"
            description={`Set amounts, a custom range, or both. ${formatCents(minAmountCents, currency, { whole: true })} to ${formatCents(maxAmountCents, currency, { whole: true })}.`}
          >
            <div className="gift-card-editor-amounts">
              {form.denominationsCents.map((cents) => (
                <Tag key={cents} size="md" onDismiss={() => removeAmount(cents)}>
                  {formatCents(cents, currency, { whole: true })}
                </Tag>
              ))}
              <div className="gift-card-editor-add-amount">
                <TextInput
                  id="gift-card-product-new-amount"
                  aria-label="Add an amount"
                  size="sm"
                  leadingText="$"
                  placeholder="Add"
                  inputMode="numeric"
                  value={newAmount}
                  onChange={(event) => setNewAmount(digitsOnly(event.target.value))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addAmount();
                    }
                  }}
                />
                <Button variant="link-color" size="sm" onClick={addAmount}>
                  Add
                </Button>
              </div>
            </div>
            {(amountError || firstError(errors, "amounts")) && (
              <p className="gift-card-editor-error">{amountError || firstError(errors, "amounts")}</p>
            )}
            <div className="gift-card-editor-divider" />
            <Toggle
              id="gift-card-product-custom-amounts"
              label="Let buyers choose an amount"
              checked={form.customAmounts}
              onChange={(value) => update({ customAmounts: checkedFrom(value) })}
            />
            {form.customAmounts && (
              <div className="gift-card-editor-row">
                <TextInput
                  id="gift-card-product-custom-min"
                  label="Minimum"
                  leadingText="$"
                  inputMode="numeric"
                  value={form.customMin}
                  onChange={(event) => update({ customMin: digitsOnly(event.target.value) })}
                />
                <TextInput
                  id="gift-card-product-custom-max"
                  label="Maximum"
                  leadingText="$"
                  inputMode="numeric"
                  value={form.customMax}
                  onChange={(event) => update({ customMax: digitsOnly(event.target.value) })}
                />
              </div>
            )}
            {firstError(errors, "customRange") && <p className="gift-card-editor-error">{firstError(errors, "customRange")}</p>}
          </Section>

          <Section title="Where it works" description="Cards keep the setting they were sold with.">
            <RadioGroup name="gift-card-product-scope" value={form.scope} onChange={(value) => update({ scope: value })}>
              <Radio
                id="gift-card-product-scope-venues"
                value={SCOPE_VENUES}
                label={<ScopeOption title="Selected venues" description="Only at the venues you pick." />}
              />
              <Radio
                id="gift-card-product-scope-team"
                value={SCOPE_TEAM}
                label={<ScopeOption title="All team venues" description="Any venue your team runs on Opendate." />}
              />
            </RadioGroup>
            {firstError(errors, "scope") && <p className="gift-card-editor-error">{firstError(errors, "scope")}</p>}
            {form.scope === SCOPE_VENUES && (
              <div className="gift-card-editor-field">
                <Select
                  id="gift-card-product-venues"
                  label="Venues"
                  multiple
                  searchable
                  placeholder="Add venues"
                  options={venueOptions}
                  value={form.venueOwnershipIds}
                  onChange={(value) => update({ venueOwnershipIds: value || [] })}
                />
                {firstError(errors, "venues") && <p className="gift-card-editor-error">{firstError(errors, "venues")}</p>}
              </div>
            )}
            <div className="gift-card-editor-field">
              <Select
                id="gift-card-product-payout-venue"
                label="Payout venue"
                options={venueOptions}
                value={form.payoutVenueOwnershipId}
                onChange={(value) => update(payoutVenueChange(value))}
                hint="Gift card sales are paid out to this venue."
              />
              {firstError(errors, "payout") && <p className="gift-card-editor-error">{firstError(errors, "payout")}</p>}
            </div>
          </Section>

          <Section title="Limits and dates">
            {expiryLimit.paidExpiryBanned && (
              <p className="gift-card-editor-hint">
                {form.expires
                  ? `Paid gift cards can't expire in ${expiryLimit.expiryStateName}. Turn this off before you change the expiry or payout venue.`
                  : `Paid gift cards can't expire in ${expiryLimit.expiryStateName}, so cards from this product never expire.`}
              </p>
            )}
            {(!expiryLimit.paidExpiryBanned || form.expires) && (
              <Toggle
                id="gift-card-product-expires"
                label="Cards expire"
                checked={form.expires}
                onChange={(value) =>
                  update({
                    expires: checkedFrom(value),
                    expiresInMonths: form.expiresInMonths || String(expiryLimit.minExpiryMonths),
                  })
                }
              />
            )}
            {form.expires && (
              <TextInput
                id="gift-card-product-expiry-months"
                label="Months after purchase"
                inputMode="numeric"
                value={form.expiresInMonths}
                onChange={(event) => update({ expiresInMonths: digitsOnly(event.target.value) })}
                hint={expiryLimit.paidExpiryBanned ? null : expiryHint(expiryLimit.minExpiryMonths, expiryLimit.expiryStateName)}
                error={firstError(errors, "expiry")}
              />
            )}
            {!form.expires && firstError(errors, "expiry") && (
              <p className="gift-card-editor-error">{firstError(errors, "expiry")}</p>
            )}
            <div className="gift-card-editor-divider" />
            <div className="gift-card-editor-row">
              <TextInput
                id="gift-card-product-stock"
                label="Stock"
                placeholder="No limit"
                inputMode="numeric"
                value={form.stockLevel}
                onChange={(event) => update({ stockLevel: digitsOnly(event.target.value) })}
                error={firstError(errors, "stock")}
              />
              <TextInput
                id="gift-card-product-per-order"
                label="Per order"
                placeholder="No limit"
                inputMode="numeric"
                value={form.perOrderLimit}
                onChange={(event) => update({ perOrderLimit: digitsOnly(event.target.value) })}
                error={firstError(errors, "perOrder")}
              />
            </div>
          </Section>

          <Section
            title="Bonus card"
            description="Give buyers a free bonus card with each card they buy at or above an amount, for a set time. The bonus is its own card with its own code and expiry."
          >
            <Toggle
              id="gift-card-product-bonus"
              label="Run a bonus"
              checked={form.bonus}
              onChange={(value) => update({ bonus: checkedFrom(value) })}
            />
            {form.bonus && (
              <>
                <div className="gift-card-editor-row">
                  <TextInput
                    id="gift-card-product-bonus-min"
                    label={`Card of at least (${currency})`}
                    inputMode="numeric"
                    value={form.bonusMin}
                    onChange={(event) => update({ bonusMin: digitsOnly(event.target.value) })}
                    error={firstError(errors, "bonusMin")}
                  />
                  <TextInput
                    id="gift-card-product-bonus-amount"
                    label={`Bonus card worth (${currency})`}
                    inputMode="numeric"
                    value={form.bonusAmount}
                    onChange={(event) => update({ bonusAmount: digitsOnly(event.target.value) })}
                    error={firstError(errors, "bonusAmount")}
                  />
                </div>
                <div className="gift-card-editor-row">
                  <DatePicker
                    id="gift-card-product-bonus-starts-on"
                    label="Starts"
                    value={form.bonusStartsOn}
                    onChange={(value) => update({ bonusStartsOn: value })}
                    error={firstError(errors, "bonusStartsOn")}
                  />
                  <DatePicker
                    id="gift-card-product-bonus-ends-on"
                    label="Ends"
                    value={form.bonusEndsOn}
                    onChange={(value) => update({ bonusEndsOn: value })}
                    hint="Leave empty to keep it running."
                    error={firstError(errors, "bonusEndsOn")}
                  />
                </div>
                <Select
                  id="gift-card-product-bonus-expiry"
                  label="Bonus card expires"
                  options={bonusExpiryChoices.map((choice) => ({ value: choice, label: bonusExpiryLabel(choice) }))}
                  value={form.bonusExpiresIn}
                  onChange={(value) => update({ bonusExpiresIn: value })}
                  hint="Dates follow the payout venue's time zone. A refund or chargeback on the purchase voids what is left on its bonus card."
                  error={firstError(errors, "bonusExpiry")}
                />
              </>
            )}
          </Section>
        </fieldset>

        <aside className="gift-card-editor-preview">
          <GiftCardPreview
            name={form.name}
            teamName={teamName}
            currency={currency}
            artworkUrl={artworkUrl}
            denominationsCents={form.denominationsCents}
            customAmounts={form.customAmounts}
            scope={form.scope}
            venueNames={selectedVenueNames}
            expiresInMonths={form.expires ? parseInt(form.expiresInMonths, 10) || null : null}
            purchasePath={product.purchasePath}
          />
        </aside>
      </div>

      <Modal
        open={confirmingPublish}
        onClose={() => setConfirmingPublish(false)}
        type="warning"
        title="Turn on gift card sales?"
        description="Gift card sales are off for your team, so buyers can't buy this gift card. Turn sales on to start selling it."
        footer={
          <div className="gift-card-editor-actions">
            <Button variant="secondary" onClick={() => setConfirmingPublish(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => confirmPublish(false)}>
              Publish only
            </Button>
            <Button variant="primary" onClick={() => confirmPublish(true)}>
              Turn on sales and publish
            </Button>
          </div>
        }
      />
    </div>
  );
};

GiftCardProductEditor.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string.isRequired,
    scope: PropTypes.string.isRequired,
    terms: PropTypes.string,
    denominationsCents: PropTypes.arrayOf(PropTypes.number),
    customMinCents: PropTypes.number,
    customMaxCents: PropTypes.number,
    payoutVenueOwnershipId: PropTypes.number,
    venueOwnershipIds: PropTypes.arrayOf(PropTypes.number),
    expiresInMonths: PropTypes.number,
    stockLevel: PropTypes.number,
    perOrderLimit: PropTypes.number,
    bonusAmountCents: PropTypes.number,
    bonusMinAmountCents: PropTypes.number,
    bonusStartsOn: PropTypes.string,
    bonusEndsOn: PropTypes.string,
    bonusExpiresInMonths: PropTypes.number,
    artworkUrl: PropTypes.string,
    purchasePath: PropTypes.string,
    soldCount: PropTypes.number,
    updatePath: PropTypes.string,
    editPath: PropTypes.string,
  }).isRequired,
  teamName: PropTypes.string.isRequired,
  currency: PropTypes.string,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  productsPath: PropTypes.string.isRequired,
  createPath: PropTypes.string.isRequired,
  minAmountCents: PropTypes.number.isRequired,
  maxAmountCents: PropTypes.number.isRequired,
  minExpiryMonths: PropTypes.number.isRequired,
  bonusExpiryChoices: PropTypes.arrayOf(PropTypes.string).isRequired,
  paidExpiryBanned: PropTypes.bool,
  expiryStateName: PropTypes.string,
  giftCardSales: giftCardSalesShape.isRequired,
  expiryLimits: PropTypes.objectOf(
    PropTypes.shape({
      minExpiryMonths: PropTypes.number,
      paidExpiryBanned: PropTypes.bool,
      expiryStateName: PropTypes.string,
    })
  ),
};

export default GiftCardProductEditor;
